import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { PortraitFrame, EditorialSelect } from '../common/editorial/EditorialControls';
import UserAvatar from '../common/UserAvatar';

const CreatePost = ({ user, topicOptions, onSubmit, onClose }) => {
  const [content, setContent] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('advice');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isImproving, setIsImproving] = useState(false);
  const [showAuraButton, setShowAuraButton] = useState(false);

  const detectAuraMention = (text) => {
    const lowerText = text.toLowerCase();
    return lowerText.includes('@aura') && lowerText.includes('write');
  };

  const extractPrompt = (text) => {
    const regex = /@aura\s+write\s+a\s+post\s+(?:about|on|explaining|regarding)\s+(.+)/i;
    const match = text.match(regex);
    return match ? match[1].trim() : null;
  };

  const handleGenerateWithAura = async () => {
    const prompt = extractPrompt(content);
    if (!prompt) {
      alert('Please use the format: @Aura write a post about [your topic]');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await axios.post('/api/community/aura/generate-post', {
        prompt,
        topic: selectedTopic,
      });
      if (response.data.content) {
        setContent(response.data.content);
        setShowAuraButton(false);
      }
    } catch (error) {
      alert('Failed to generate content. Please try again or write manually.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleImproveWithAura = async () => {
    if (!content.trim()) {
      alert('Please write some content first.');
      return;
    }

    setIsImproving(true);
    try {
      const response = await axios.post('/api/community/aura/improve-post', {
        original_content: content.trim(),
        topic: selectedTopic,
      });
      if (response.data.improved_content) {
        setContent(response.data.improved_content);
      }
    } catch (error) {
      alert('Failed to improve content. Please try again.');
    } finally {
      setIsImproving(false);
    }
  };

  const handleContentChange = (e) => {
    const newContent = e.target.value;
    setContent(newContent);
    setShowAuraButton(detectAuraMention(newContent));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!content.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const payload = {
        content: content.trim(),
        topic: String(selectedTopic || '').toLowerCase(),
        anonymous: false,
      };

      const response = await axios.post('/api/community/posts', payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.data.status === 'success') {
        await onSubmit(response.data.data.post);
        onClose();
      } else {
        alert('Post creation failed. Please try again.');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to create post. Please try again.';
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const userName = user?.full_name || user?.profile_name || 'You';
  const userInitials = userName.charAt(0).toUpperCase();

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="modal-overlay"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 6 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="surface-panel w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-6 border-b border-hairline">
            <h2 className="font-display text-h2 text-primary">Create post</h2>
            <button type="button" onClick={onClose} className="icon-btn" aria-label="Close">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            <div className="flex items-center gap-3 mb-5">
              <UserAvatar
                userId={user?.user_id}
                src={user?.avatar_url || user?.avatar}
                alt={userName}
                size="xs"
              />
              <div>
                <h3 className="text-body font-medium text-primary">{userName}</h3>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-label text-secondary">Topic</span>
                  <EditorialSelect
                    value={selectedTopic}
                    onChange={(e) => setSelectedTopic(e.target.value)}
                    className="w-auto min-w-[140px] py-1 text-label"
                  >
                    {topicOptions.filter((t) => t.id !== 'all').map((topic) => (
                      <option key={topic.id} value={topic.id}>
                        {topic.label}
                      </option>
                    ))}
                  </EditorialSelect>
                </div>
              </div>
            </div>

            <textarea
              value={content}
              onChange={handleContentChange}
              placeholder="What's on your mind? Try @Aura write a post about [topic] for AI help."
              className="input-editorial resize-none min-h-[160px] mb-2"
              rows={6}
              maxLength={1000}
              autoFocus
            />
            <div className="flex justify-between items-center mb-5">
              <span className="text-label text-faint">{content.length}/1000</span>
              {showAuraButton && (
                <button
                  type="button"
                  onClick={handleGenerateWithAura}
                  disabled={isGenerating}
                  className="btn-secondary text-label flex items-center gap-2"
                >
                  <SparklesIcon className="w-4 h-4" />
                  {isGenerating ? 'Generating…' : 'Generate with Aura'}
                </button>
              )}
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-hairline">
              <div className="flex items-center gap-2">
                {content.trim() && !showAuraButton && (
                  <button
                    type="button"
                    onClick={handleImproveWithAura}
                    disabled={isImproving || isGenerating}
                    className="btn-ghost text-label flex items-center gap-2"
                  >
                    <SparklesIcon className="w-4 h-4" />
                    {isImproving ? 'Improving…' : 'Aura write'}
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={onClose} className="btn-ghost">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!content.trim() || isSubmitting}
                  className="btn-primary"
                >
                  {isSubmitting ? 'Posting…' : 'Post'}
                </button>
              </div>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CreatePost;
